import { AlertTriangle, CheckCircle2, Search, Users, XCircle } from "lucide-react";
import { useState } from "react";
import axios from "axios";
import BONavigation from "../../../components/admin/BONavigation";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

interface FounderInfo {
    nom?: string;
    prenom?: string;
    fullName?: string;
    firstName?: string;
    lastName?: string;
    age?: number;
    dateNaissance?: string;
    qualite?: string;
    role?: string;
    type?: "physical" | "moral";
}

interface SourceResult {
    name: string;
    ok: boolean;
    count?: number;
    error?: string;
}

interface MultiSiteOwner {
    personId: string;
    otherEstablishmentCount: number;
}

interface CheckOwnerResult {
    establishmentId: string;
    siren: string;
    status: "ok" | "no_data" | "error";
    founders: FounderInfo[];
    sources: SourceResult[];
    signalCreated: boolean;
    linkedPersonIds: string[];
    multiSiteTagged: boolean;
    multiSiteOwners: MultiSiteOwner[];
    rawApiResponses: Record<string, any>;
}

function founderName(f: FounderInfo): string {
    const fromParts = `${f.firstName || f.prenom || ""} ${f.lastName || f.nom || ""}`.trim();
    return f.fullName || fromParts || "—";
}

const statusLabels: Record<CheckOwnerResult["status"], { label: string; className: string }> = {
    ok: { label: "Fondateur(s) trouvé(s)", className: "text-green-700 bg-green-50 border-green-200" },
    no_data: { label: "Aucune donnée trouvée", className: "text-yellow-700 bg-yellow-50 border-yellow-200" },
    error: { label: "Échec (toutes les sources ont échoué)", className: "text-red-700 bg-red-50 border-red-200" },
};

export default function FounderEnrichment() {
    const [siren, setSiren] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CheckOwnerResult | null>(null);
    const [showRaw, setShowRaw] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = siren.trim();
        if (!/^\d{9}$/.test(trimmed)) {
            setError("Le SIREN doit contenir exactement 9 chiffres.");
            return;
        }
        setError(null);
        setResult(null);
        setShowRaw(false);
        setLoading(true);
        try {
            const { data } = await axios.post(
                `${API_URL}/establishments/admin/${trimmed}/check-owner`,
                {},
                { headers: authHeaders() }
            );
            setResult(data);
        } catch (err: any) {
            setError(err?.response?.data?.error || err?.response?.data?.message || "Une erreur est survenue.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F5F0EB]">
            <div className="mx-auto max-w-7xl px-4">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-xl font-semibold text-black">Enrichissement fondateur</h1>
                    <p className="text-xs text-gray-600 mt-0.5">
                        Relancer une recherche INSEE + BODACC + RNE pour un SIREN et lier le(s) fondateur(s) trouvé(s) à l'établissement
                    </p>
                </div>

                <BONavigation />

                {/* Form */}
                <form onSubmit={handleSubmit} className="mb-6">
                    <div className="bg-white border border-gray-200 rounded-sm p-5">
                        <div className="flex items-end gap-3">
                            <div className="flex-1">
                                <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                                    SIREN
                                </label>
                                <input
                                    type="text"
                                    value={siren}
                                    onChange={e => setSiren(e.target.value.replace(/\D/g, "").slice(0, 9))}
                                    placeholder="123456789"
                                    className="w-full border border-gray-200 rounded-sm px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-200"
                                    maxLength={9}
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex items-center gap-2 px-5 py-2 bg-black text-white text-sm font-medium rounded-sm hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                {loading ? (
                                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                ) : (
                                    <Search className="w-4 h-4" />
                                )}
                                {loading ? "Recherche..." : "Enrichir"}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <p className="mt-2 text-xs text-red-600">{error}</p>
                    )}
                </form>

                {/* Results */}
                {result && (
                    <div className="space-y-6 pb-10">
                        {/* Status banner */}
                        <div className={`flex items-center justify-between border rounded-sm px-4 py-3 ${statusLabels[result.status].className}`}>
                            <span className="text-sm font-medium">{statusLabels[result.status].label}</span>
                            <span className="text-xs font-mono opacity-70">SIREN {result.siren}</span>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {[
                                { label: "Fondateurs trouvés", value: result.founders.length, highlight: result.founders.length > 0 },
                                { label: "Personnes liées", value: result.linkedPersonIds.length, highlight: result.linkedPersonIds.length > 0 },
                                { label: "Signal changement propriétaire", value: result.signalCreated ? "Oui" : "Non", highlight: result.signalCreated },
                                { label: "Multi-site détecté", value: result.multiSiteTagged ? "Oui" : "Non", highlight: result.multiSiteTagged },
                            ].map(stat => (
                                <div key={stat.label} className={`border rounded-sm p-4 ${
                                    stat.highlight ? "border-green-200 bg-green-50/40" : "border-gray-200 bg-white"
                                }`}>
                                    <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
                                    <p className="text-xs mt-0.5 text-gray-500">{stat.label}</p>
                                </div>
                            ))}
                        </div>

                        {/* Sources */}
                        <div>
                            <div className="flex items-center gap-2 mb-3">
                                <Search className="w-4 h-4 text-gray-500" />
                                <h2 className="text-sm font-semibold text-gray-900">Sources interrogées</h2>
                            </div>
                            <div className="border border-gray-200 rounded-sm overflow-hidden">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-200">
                                            <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Source</th>
                                            <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Résultat</th>
                                            <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Fondateurs</th>
                                            <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Erreur</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {result.sources.map((s, i) => (
                                            <tr key={s.name} className={`border-b border-gray-100 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                                                <td className="px-4 py-2.5 font-medium text-gray-900 uppercase">{s.name}</td>
                                                <td className="px-4 py-2.5">
                                                    {s.ok ? (
                                                        <span className="inline-flex items-center gap-1 text-green-700">
                                                            <CheckCircle2 className="w-3.5 h-3.5" /> OK
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-red-700">
                                                            <XCircle className="w-3.5 h-3.5" /> Échec
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-gray-700">{s.count ?? "—"}</td>
                                                <td className="px-4 py-2.5 text-red-500">{s.error || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        {/* Founders */}
                        {result.founders.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <Users className="w-4 h-4 text-gray-500" />
                                    <h2 className="text-sm font-semibold text-gray-900">Fondateurs / dirigeants ({result.founders.length})</h2>
                                </div>
                                <div className="border border-gray-200 rounded-sm overflow-hidden">
                                    <table className="w-full text-xs">
                                        <thead>
                                            <tr className="bg-gray-50 border-b border-gray-200">
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Nom</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Qualité</th>
                                                <th className="text-left px-4 py-2.5 font-semibold text-gray-600">Naissance</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {result.founders.map((f, i) => (
                                                <tr key={i} className={`border-b border-gray-100 last:border-0 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}>
                                                    <td className="px-4 py-2.5 text-gray-900 font-medium">{founderName(f)}</td>
                                                    <td className="px-4 py-2.5 text-gray-500">{f.qualite || f.role || "—"}</td>
                                                    <td className="px-4 py-2.5 text-gray-500">{f.dateNaissance || (f.age != null ? `${f.age} ans` : "—")}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Multi-site owners */}
                        {result.multiSiteOwners.length > 0 && (
                            <div>
                                <div className="flex items-center gap-2 mb-3">
                                    <AlertTriangle className="w-4 h-4 text-gray-500" />
                                    <h2 className="text-sm font-semibold text-gray-900">Propriétaires multi-établissements</h2>
                                </div>
                                <div className="border border-gray-200 rounded-sm overflow-hidden divide-y divide-gray-100">
                                    {result.multiSiteOwners.map(o => (
                                        <div key={o.personId} className="px-4 py-2.5 flex items-center justify-between text-xs">
                                            <span className="font-mono text-gray-700">Person #{o.personId}</span>
                                            <span className="text-gray-500">{o.otherEstablishmentCount} autre(s) établissement(s)</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Raw API responses (debug) */}
                        <div>
                            <button
                                onClick={() => setShowRaw(v => !v)}
                                className="text-xs text-gray-500 hover:text-gray-700 transition-colors mb-2 underline"
                            >
                                {showRaw ? "Masquer" : "Afficher"} les réponses brutes des APIs (debug)
                            </button>
                            {showRaw && (
                                <pre className="border border-gray-200 rounded-sm bg-gray-900 text-gray-100 text-[11px] p-4 overflow-x-auto max-h-96 overflow-y-auto">
                                    {JSON.stringify(result.rawApiResponses, null, 2)}
                                </pre>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
